import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, 
  Zap, 
  Target, 
  CheckCircle2,
  CircleDot
} from 'lucide-react';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6, ease: 'easeOut' }
  }
};

export const MethodologyPage = () => {
  return (
    <div data-testid="methodology-page">
      {/* Hero Section */}
      <section className="section-padding pt-32 bg-gradient-to-br from-slate-50 to-teal-50" data-testid="methodology-hero">
        <div className="container-main">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="max-w-4xl"
          >
            <motion.span 
              variants={itemVariants}
              className="font-mono text-xs uppercase tracking-widest text-teal-600 mb-6 block"
            >
              Our Methodology
            </motion.span>
            
            <motion.h1 
              variants={itemVariants}
              className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-[1.1] mb-6"
            >
              Value Innovation & Evidence-Based Complexity
            </motion.h1>
            
            <motion.p 
              variants={itemVariants}
              className="text-lg md:text-xl text-slate-600 leading-relaxed"
            >
              At HSL, we reject the prevailing notion that health excellence is strictly 
              a function of exponential spending. We recognize that in resource-constrained environments, 
              the path to transformation lies in <span className="text-teal-600 font-semibold">Value Innovation</span>—the 
              simultaneous pursuit of increased stakeholder value and the radical reduction of systemic 
              complexity and waste.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* HSL Impact Model */}
      <section className="section-padding bg-white" data-testid="impact-model-section">
        <div className="container-main">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <span className="font-mono text-xs uppercase tracking-widest text-slate-500 mb-4 block">
              The HSL Impact Model
            </span>
            <h2 className="font-heading text-3xl md:text-5xl font-semibold text-slate-900 mb-6">
              Pillars of Transformation
            </h2>
            <p className="text-slate-600 text-lg">
              Our methodology is designed to move systems from "Default" states—where outcomes 
              are accidental—to "Designed" states, where success is engineered.
            </p>
          </motion.div>

          {/* Impact Model Visualization */}
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Systemic Leapfrogging */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-slate-50 border border-slate-200 p-8 lg:p-10 hover:border-teal-500 hover:shadow-lg transition-all"
              data-testid="pillar-leapfrogging"
            >
              <div className="flex items-start gap-4 mb-6">
                <div className="w-14 h-14 bg-teal-100 border border-teal-200 flex items-center justify-center flex-shrink-0">
                  <Zap size={28} className="text-teal-600" />
                </div>
                <div>
                  <span className="font-mono text-xs uppercase tracking-widest text-slate-500 block mb-1">
                    Pillar 01
                  </span>
                  <h3 className="font-heading text-2xl text-slate-900">
                    Systemic Leapfrogging
                  </h3>
                </div>
              </div>
              
              <p className="text-slate-600 mb-6 leading-relaxed">
                Traditional development models assume a linear progression. We challenge this 
                by identifying technological and policy "shortcuts."
              </p>

              <div className="space-y-4 mb-6">
                <div className="flex items-start gap-3">
                  <CircleDot size={16} className="text-teal-600 mt-1 flex-shrink-0" />
                  <div>
                    <span className="text-slate-900 font-medium">Mechanism:</span>
                    <p className="text-slate-500 text-sm">
                      Integrating AI-driven diagnostics, digital governance, and decentralized 
                      delivery models early in the design phase.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 size={16} className="text-teal-600 mt-1 flex-shrink-0" />
                  <div>
                    <span className="text-slate-900 font-medium">Outcome:</span>
                    <p className="text-slate-500 text-sm">
                      Accelerated health gains that decouple progress from legacy infrastructure requirements.
                    </p>
                  </div>
                </div>
              </div>

              <div className="h-1 w-full bg-slate-200 overflow-hidden">
                <motion.div 
                  initial={{ width: 0 }}
                  whileInView={{ width: '75%' }}
                  viewport={{ once: true }}
                  transition={{ duration: 1, delay: 0.3 }}
                  className="h-full bg-gradient-to-r from-teal-500 to-teal-400"
                />
              </div>
            </motion.div>

            {/* Strategic Divergence */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-slate-50 border border-slate-200 p-8 lg:p-10 hover:border-teal-500 hover:shadow-lg transition-all"
              data-testid="pillar-divergence"
            >
              <div className="flex items-start gap-4 mb-6">
                <div className="w-14 h-14 bg-teal-100 border border-teal-200 flex items-center justify-center flex-shrink-0">
                  <Target size={28} className="text-teal-600" />
                </div>
                <div>
                  <span className="font-mono text-xs uppercase tracking-widest text-slate-500 block mb-1">
                    Pillar 02
                  </span>
                  <h3 className="font-heading text-2xl text-slate-900">
                    Strategic Divergence
                  </h3>
                </div>
              </div>
              
              <p className="text-slate-600 mb-6 leading-relaxed">
                Incremental improvements in contested, low-impact policy areas often yield 
                diminishing returns. HSL practices Strategic Divergence.
              </p>

              <div className="space-y-4 mb-6">
                <div className="flex items-start gap-3">
                  <CircleDot size={16} className="text-teal-600 mt-1 flex-shrink-0" />
                  <div>
                    <span className="text-slate-900 font-medium">Mechanism:</span>
                    <p className="text-slate-500 text-sm">
                      Pivoting focus toward "Unserved Frontiers"—neglected demographics, geographic 
                      outliers, and overlooked systemic bottlenecks.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 size={16} className="text-teal-600 mt-1 flex-shrink-0" />
                  <div>
                    <span className="text-slate-900 font-medium">Outcome:</span>
                    <p className="text-slate-500 text-sm">
                      Creating "Blue Oceans" of health equity with the highest possible ROI for population health.
                    </p>
                  </div>
                </div>
              </div>

              <div className="h-1 w-full bg-slate-200 overflow-hidden">
                <motion.div 
                  initial={{ width: 0 }}
                  whileInView={{ width: '85%' }}
                  viewport={{ once: true }}
                  transition={{ duration: 1, delay: 0.3 }}
                  className="h-full bg-gradient-to-r from-teal-500 to-teal-400"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Result Section */}
      <section className="section-padding bg-teal-600" data-testid="result-section">
        <div className="container-main">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto text-center"
          >
            <span className="font-mono text-xs uppercase tracking-widest text-teal-200 mb-4 block">
              The HSL Result
            </span>
            <h2 className="font-heading text-3xl md:text-5xl font-semibold text-white mb-6">
              Designed Success
            </h2>
            <p className="text-teal-100 text-lg leading-relaxed mb-10">
              By combining Value Innovation with deep analytical rigor, HSL 
              ensures that health transformation is not a matter of luck, but a result of
              <span className="text-white font-semibold"> evidence-based architecture</span>.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/leadership"
                className="bg-white text-teal-600 px-8 py-4 font-bold tracking-wide uppercase text-sm hover:bg-teal-50 transition-colors inline-flex items-center justify-center gap-2 group"
                data-testid="result-cta-leadership"
              >
                Leadership Development
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/contact"
                className="bg-transparent text-white border border-white/50 px-8 py-4 font-medium uppercase text-sm hover:bg-white/10 transition-colors inline-flex items-center justify-center gap-2"
                data-testid="result-cta-contact"
              >
                Start a Conversation
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
